//@ts-check

import { useState, useEffect, useContext } from 'react';

import { getViewMode } from 'store/auth';
import { useSelector } from 'react-redux';
import { PermissionContext } from 'components/Permission/permission-wrapper';
import { USER_METHODS, USER_ROLES } from 'constants/constants';
import { useUser } from 'hooks';

import { showToastInfo } from '@core/components/toastify/ToastTypes';
import Router from 'next/router';

/**
 * @param {PermissionMethodsProps[]} methodAndRoute
 * @returns {[Map<methodAndRoute[number]["mapKey"], methodAndRoute[number] & { allowed: boolean }>, React.Dispatch<React.SetStateAction<Map<methodAndRoute[number]["mapKey"], methodAndRoute[number] & { allowed: boolean }>>> ]}
 */
export const usePermissions = (methodAndRoute) => {
  const auth = useUser();
  const allowedPermissions = useContext(PermissionContext);
  const viewMode = useSelector(getViewMode);

  const currentViewMode =
    auth.roles?.includes(USER_ROLES.SUPER_ADMIN) &&
    USER_ROLES.ADMIN === viewMode
      ? USER_ROLES.SUPER_ADMIN
      : viewMode;

  const [permissions, setPermissions] = useState(new Map([]));
  const [hasRedirected, setHasRedirected] = useState(false); // Track redirection

  useEffect(() => {
    if (hasRedirected) return; // Prevent multiple redirects

    const calculatedPermissions = new Map();

    if (allowedPermissions?.size) {
      methodAndRoute.forEach(
        // @ts-ignore
        ({ method, route, name, mapKey, redirect, ...props }) => {
          const permissionKey = `${(
            name || ''
          ).toUpperCase()}-${currentViewMode}-${(
            method || ''
          ).toUpperCase()}-${route}`;

          if (redirect && !allowedPermissions.has(permissionKey)) {
            Router.replace('/401', '/');
            showToastInfo('You have no access to this Page');
            setHasRedirected(true); // Mark as redirected
            return; // Stop further processing
          }

          calculatedPermissions.set(mapKey, {
            method,
            route,
            name,
            mapKey,
            ...props,
            allowed: allowedPermissions.has(permissionKey),
          });
        },
      );
    }

    setPermissions(calculatedPermissions);
  }, [
    methodAndRoute?.length,
    allowedPermissions?.size,
    currentViewMode,
    hasRedirected,
  ]);

  return [permissions, setPermissions];
};

/**
 * @typedef {{method: keyof USER_METHODS;  route: string; name?: string; mapKey?: string}} PermissionMethodsProps
 */
